import React, { useState, useEffect } from "react";
import { Modal, Form, Button, SelectPicker, Input, Checkbox } from "rsuite";
import FileDownloadIcon from "@rsuite/icons/FileDownload";
import CheckIcon from "@rsuite/icons/Check";
import ReactGA from "react-ga4";
import "./index.css";

const KRISHNAKALI_USER_ID = "U06HATH4N3S";
const PURVI_USER_ID = "U065Z3ZN98R";

const Index = ({ modalOpen, setModalOpen }) => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [enquiryPurpose, setEnquiryPurpose] = useState("");
  const [remarks, setRemarks] = useState("");
  const [optInValue, setOptInValue] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resumeLink, setResumeLink] = useState("");

  const handleClose = () => {
    setModalOpen(false);
  };

  useEffect(() => {
    // 1) get the raw query string, e.g. "?purpose=AI+For+Factory&foo=bar"
    const qs = window.location.search;
    // 2) extract the "purpose" param (capturing everything up to the next & or end)
    const match = qs.match(/(?:[?&])purpose=([^&]+)/);
    if (!match) return; // nothing to do if no "purpose" in URL

    // 3) URL‑decode and turn "+" into spaces
    const extracted = decodeURIComponent(match[1].replace(/\+/g, " "));
    // 4) set it into your state
    if (extracted) setEnquiryPurpose(extracted);
    return () => {
      const url = new URL(window.location.href);
      url.searchParams.delete("purpose");
      // replaceState to avoid a reload
      window.history.replaceState(null, "", url.toString());
      setEnquiryPurpose("");
    };
  }, [modalOpen]);

  const optionsData = [
    "MarketPlace",
    "MetalCloud",
    "Careers",
    "Request Demo",
    "AI For Factory",
    "Others",
  ].map((items) => ({ label: items, value: items }));

  /**
   * Validates the form fields for the enquiry form and returns validation status.
   *
   * The function checks the following criteria:
   * - Name must only contain alphabets and spaces.
   * - Phone must only contain numeric characters.
   * - Enquiry purpose must be selected.
   * - Remarks cannot be empty.
   *
   * If any field is invalid, an alert is displayed with the first validation error encountered.
   *
   * @returns {boolean} - Returns `true` if all fields are valid, otherwise `undefined`.
   *
   */
  const validateFields = () => {
    const newErrors = {};
    // Validate form fields
    const namePattern = /^[a-zA-Z\s]*$/;
    const phonePattern = /^\d+$/;

    if (!name || !namePattern.test(name)) {
      newErrors.name = "Please enter a valid name (alphabets and spaces only).";
    }

    if (!phone || !phonePattern.test(phone)) {
      newErrors.phone = "Please enter a valid phone number (numbers only).";
    }

    if (!enquiryPurpose) {
      newErrors.enquiryPurpose = "Please select an enquiry purpose.";
    }

    if (!remarks) {
      newErrors.remarks = "Remarks cannot be empty.";
    }

    if (Object.keys(newErrors).length !== 0) {
      alert(Object.values(newErrors)[0]);
    } else return true; // Return true if no errors
  };

  const handleSubmit = async (e) => {
    if (isSubmitting) return;

    if (!validateFields()) return;

    setIsSubmitting(true); // Disable the button

    const payload = {
      text: `Enquiry from NowPurchase Website : ${name}`,
      blocks: [
        {
          type: "section",
          text: {
            type: "mrkdwn",
            text: `<@${
              enquiryPurpose === "Careers" ? KRISHNAKALI_USER_ID : PURVI_USER_ID
            }> You have a new enquiry: ✨`,
          },
        },
        {
          type: "section",
          fields: [
            {
              type: "mrkdwn",
              text: `*Name:*\n${name}`,
            },
            {
              type: "mrkdwn",
              text: `*Contact Number:*\n${phone}`,
            },
            {
              type: "mrkdwn",
              text: `*Enquiry Purpose:*\n${
                enquiryPurpose || "No purpose specified"
              }`,
            },
            {
              type: "mrkdwn",
              text: `*Remarks:*\n${remarks || "No remarks provided"}`,
            },
            {
              type: "mrkdwn",
              text: `*WhatsApp Notifications:*\n${optInValue ? "Yes" : "No"}`,
            },
            ...(enquiryPurpose === "Careers"
              ? [
                  {
                    type: "mrkdwn",
                    text: `*Resume Link:*\n${resumeLink || "No link provided"}`,
                  },
                ]
              : []),
          ],
        },
      ],
    };

    const slackPayload = {
      text: `New enquiry:
        Name: ${name}
        Contact Number: ${phone}
        Enquiry Purpose: ${enquiryPurpose}
        Remarks: ${remarks}`,
    };

    try {
      await notifyOnSlack("NP_WEBSITE", slackPayload.text);
      await fetch(`${process.env.REACT_APP_GSCRIPT_URL}?function=doPost`, {
        method: "POST",
        mode: "no-cors",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name,
          contactNumber: phone,
          enquiryPurpose,
          remarks,
          optInWhatsApp: optInValue,
          resumeLink,
        }),
      });

      // Track form submission in GA4
      ReactGA.event("form_submit", {
        experiment_name: "connect_with_us_ab_test",
        variant_name: "form",
      });

      setModalOpen(false);
    } catch (error) {
      alert("Error submitting enquiry");
      setIsSubmitting(false);
    } finally {
      setName("");
      setPhone("");
      setEnquiryPurpose("");
      setRemarks("");
      setResumeLink("");
      setTimeout(() => {
        setIsSubmitting(false);
      }, 60000);
    }
  };

  const notifyOnSlack = async (channel, message) => {
    try {
      const response = await fetch(
        "https://test-api.nowpurchase.com/api/send_slack/",
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            channel: channel,
            message: message,
          }),
        }
      );
      if (!response.ok) {
        throw new Error("Failed to submit enquiry");
      }

      return response;
    } catch (error) {
      console.error("Fetch error:", error);
      throw error;
    }
  };

  const handleDownload = () => {
    window.open(
      "https://images.nowpurchase.com/uploads/NP+Deck+-+Jan'26.pdf",
      "_blank"
    );
  };
  return (
    <Modal open={modalOpen} onClose={handleClose}>
      <Modal.Header>
        <Modal.Title>Brochure Download / Enquiry Form</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form fluid onSubmit={handleSubmit}>
          <Form.Group>
            <Form.ControlLabel for="name">
              <span>Name</span>
            </Form.ControlLabel>
            <Form.Control
              name="name"
              type="text"
              placeholder="Enter your name here"
              Required
              value={name}
              onChange={(value) => setName(value)}
            />
            {/* <Form.HelpText>Required</Form.HelpText> */}
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel for="phone">
              <span>Contact Number</span>
            </Form.ControlLabel>
            <Form.Control
              name="phone"
              type="tel"
              placeholder="Enter your contact number"
              value={phone}
              onChange={(value) => setPhone(value)}
              required
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel for="entry_purpose">
              <span>Enquiry Purpose</span>
            </Form.ControlLabel>

            <SelectPicker
              data={optionsData}
              value={enquiryPurpose}
              placeholder="Select Enquiry Purpose"
              searchable={false}
              size="sm"
              className="modalSelectPicker"
              onChange={(value) => setEnquiryPurpose(value)}
              style={{
                display: "flex",
                fontSize: "12px",
              }}
            />
          </Form.Group>

          {enquiryPurpose === "Careers" && (
            <Form.Group>
              <Form.ControlLabel for="resume_link">
                <span>Resume Link</span>
              </Form.ControlLabel>
              <Input
                value={resumeLink}
                required
                onChange={(value) => setResumeLink(value)}
                placeholder="Enter your resume link here"
              />
            </Form.Group>
          )}
          <Form.Group>
            <Form.ControlLabel for="remark">
              <span>Remark</span>
            </Form.ControlLabel>
            <Input
              value={remarks}
              onChange={(value) => setRemarks(value)}
              as="textarea"
              name="remark"
              rows={7}
              placeholder="Enter your remarks here"
            />
          </Form.Group>
          <Form.Group>
            <Checkbox
              onChange={() => setOptInValue((val) => !val)}
              value={optInValue}
              defaultChecked
            >
              Opt in for WhatsApp Notfications
            </Checkbox>
          </Form.Group>

          <Form.Group className="enquiryActions">
            <Button appearance="subtle" onClick={handleDownload}>
              <FileDownloadIcon /> Download Brochure
            </Button>
            <Button disabled={isSubmitting} type="submit">
              <CheckIcon /> Submit Enquiry
            </Button>
          </Form.Group>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default Index;
